import {
  Action,
  ActionType,
  CatalogState,
  DashboardItem,
  FilterType,
} from './types';
import { FILTERS } from './filters';
import Order from '@lib/Order';
import * as JsSearch from 'js-search';

export const initialState = {
  catalogId: null,
  search: null,
  items: [],
  order: null,
  allItems: [],
  benefit: {},
  selectedFilterIndex: null,
};

const indexSearch = (items: [DashboardItem]) => {
  const search = new JsSearch.Search('id');
  search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
  search.addIndex('title');
  search.addIndex('tags');
  search.addDocuments(items);
  return search;
};

const filterItemsByAmount = (items: [DashboardItem], amount: number) => {
  return items.filter((item) => parseFloat(item.price) <= amount);
};

const reducer = (state: CatalogState, action: Action) => {
  switch (action.type) {
    case ActionType.SET_INITIAL_DATA:
      if (!action.value.items || !action.value.items.length) {
        return { ...state };
      }

      const search = indexSearch(action.value.items);

      const benefit = action.value.benefit;
      const order = new Order({
        maxAmount: benefit.benefit_info.otc_benefit_max_amnt,
      });

      return {
        ...state,
        order: order,
        catalogId: action.value.id,
        search: search,
        benefit: benefit,
        selectedFilterIndex: 0,
        items: search.search('favorites'),
        allItems: action.value.items,
      };
    case ActionType.SEARCH_ITEMS:
      if (!state.search) {
        return state;
      }

      // If search is empty, clear search results
      if (!action.value.searchInput) {
        return {
          ...state,
          selectedFilterIndex: null,
          items: state.allItems,
        };
      }

      // Search items by search input
      return {
        ...state,
        items: state.search.search(action.value.searchInput),
      };
    case ActionType.FILTER_ITEMS:
      if (!state.search) {
        return state;
      }

      // Disable filter
      if (action.value.index === state.selectedFilterIndex) {
        return {
          ...state,
          selectedFilterIndex: null,
          items: state.allItems,
        };
      }

      const filter = FILTERS[action.value.index];

      // Filter by price filter (e.g. for amount remaining)
      if (filter.type === FilterType.PRICE) {
        return {
          ...state,
          selectedFilterIndex: action.value.index,
          items: filterItemsByAmount(
            state.allItems,
            state.order?.remainingAmount
          ),
        };
      }

      // Filter by tag name
      return {
        ...state,
        selectedFilterIndex: action.value.index,
        items: state.search.search(filter.name),
      };
    case ActionType.DECREMENT_ITEM:
      state.order.decrement(action.value.itemId);

      // Persist PRICE filter when decrementing an item.
      if (FILTERS[state.selectedFilterIndex]?.type === FilterType.PRICE) {
        return {
          ...state,
          items: filterItemsByAmount(
            state.allItems,
            state.order?.remainingAmount
          ),
        };
      }

      return {
        ...state,
      };
    case ActionType.INCREMENT_ITEM:
      const item = state.allItems.find(
        (item) => item.id === action.value.itemId
      );
      state.order.increment(action.value.itemId, item);

      // Persist PRICE filter when incrementing an item.
      if (FILTERS[state.selectedFilterIndex]?.type === FilterType.PRICE) {
        return {
          ...state,
          items: filterItemsByAmount(
            state.allItems,
            state.order?.remainingAmount
          ),
        };
      }

      return {
        ...state,
      };
    case ActionType.REMOVE_ITEM:
      state.order.remove(action.value.itemId);

      // Persist PRICE filter when removing an item.
      if (FILTERS[state.selectedFilterIndex]?.type === FilterType.PRICE) {
        return {
          ...state,
          items: filterItemsByAmount(
            state.allItems,
            state.order?.remainingAmount
          ),
        };
      }

      return {
        ...state,
      };
    default:
      throw new Error();
  }
};

export default reducer;
